import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DailyReadingCard = ({ 
  currentDay, 
  selectedDay, 
  reading, 
  onPreviousDay, 
  onNextDay, 
  onDaySelect, 
  onResetToToday,
  isPostCurriculum = false
}) => {
  const [showDaySelector, setShowDaySelector] = useState(false);

  // Generate day options for dropdown - limit to curriculum days only
  const dayOptions = Array.from({ length: 42 }, (_, i) => i + 1);

  const handleDaySelect = (day) => {
    onDaySelect(day);
    setShowDaySelector(false);
  };

  const isCurrentDay = selectedDay === currentDay;
  const canNavigateBack = selectedDay > 1;
  const canNavigateForward = selectedDay < 42; // Keep navigation within curriculum

  // Don't render the reading card if we're in post-curriculum mode
  if (isPostCurriculum) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-gentle p-6 mb-6">
      {/* Enhanced Day Progress Header with Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name="BookOpen" size={20} color="white" />
          </div>
          <div>
            {/* Day Navigation Controls */}
            <div className="flex items-center space-x-2 mb-1">
              <button
                onClick={onPreviousDay}
                disabled={!canNavigateBack}
                className={`p-1 rounded transition-colors duration-200 ${
                  canNavigateBack 
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted' 
                    : 'text-muted-foreground/50 cursor-not-allowed'
                }`}
                title="Previous day"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>
              
              {/* Day Selector Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowDaySelector(!showDaySelector)}
                  className="flex items-center space-x-1 px-2 py-1 rounded hover:bg-muted transition-colors duration-200"
                >
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    Day {selectedDay} of 42
                  </h2>
                  <Icon name="ChevronDown" size={14} color="current" />
                </button>

                {/* Dropdown Menu */}
                {showDaySelector && (
                  <div className="absolute top-full left-0 mt-2 w-64 max-h-64 overflow-y-auto bg-card border border-border rounded-lg shadow-lg z-50">
                    <div className="p-2 grid grid-cols-7 gap-1">
                      {dayOptions?.map((day) => (
                        <button
                          key={day}
                          onClick={() => handleDaySelect(day)}
                          className={`p-2 text-sm rounded transition-colors duration-200 ${
                            day === selectedDay
                              ? 'bg-primary text-primary-foreground'
                              : day === currentDay
                                ? 'bg-muted text-foreground font-semibold'
                                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                          title={day === currentDay ? 'Today' : `Day ${day}`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={onNextDay}
                disabled={!canNavigateForward}
                className={`p-1 rounded transition-colors duration-200 ${
                  canNavigateForward 
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted' 
                    : 'text-muted-foreground/50 cursor-not-allowed'
                }`}
                title="Next day"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <p className="text-sm text-muted-foreground">
                {isCurrentDay ? 'Today\'s Reading' : 'Reading Archive'}
              </p>
              {!isCurrentDay && (
                <button
                  onClick={onResetToToday}
                  className="text-xs text-primary hover:text-primary/80 underline transition-colors duration-200"
                >
                  Return to Today
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(Math.min(selectedDay, 42) / 42) * 100}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground font-mono">
            {Math.round((Math.min(selectedDay, 42) / 42) * 100)}%
          </span>
        </div>
      </div>

      {/* Reading Content - only show if reading exists */}
      {reading && (
        <>
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              {reading?.title}
            </h3>
            <div className="prose prose-sm max-w-none text-foreground">
              {reading?.content?.split('\n')?.map((paragraph, index) => (
                <p key={index} className="mb-3 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Enhanced Reading Actions */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span className="text-sm">~3 min read</span>
              </div>
              {!isCurrentDay && (
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Archive" size={16} />
                  <span className="text-sm">Archive</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button className="flex items-center space-x-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
                <Icon name="Heart" size={16} />
                <span>Reflect</span>
              </button>
              <button className="flex items-center space-x-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
                <Icon name="Share2" size={16} />
                <span>Share</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Click outside to close dropdown */}
      {showDaySelector && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowDaySelector(false)}
        />
      )}
    </div>
  );
};

export default DailyReadingCard;